import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import { categoryLabel, stateLabel } from "./plannerLabels"
import { OPEN_STATES, countryTimezone } from "./plannerQuery"
import { CONFIRMED_FIELDS, addedSources, changesOf, draftOf, editorQuery, fieldsOf, formErrors, formOf, newForm } from "./proposalForm"
import { RELOADING_CODES, UNCONFIRMED } from "./proposalMessages"
import { valueSource } from "./proposalHistory"
import { SEND_FIELD_LABELS, missingToSend } from "./ItemState"
import { openWarnings } from "./plannerItems"
import useProposalOperation from "./useProposalOperation"
import useProposalRead from "./useProposalRead"
import ProposalEventFields from "./ProposalEventFields"
import ProposalCompetitions from "./ProposalCompetitions"
import { AnswerTab, HistoryTab, WarningsTab } from "./ProposalRecord"
import { PreviousEditionDialog, PreviousEditionLine, useDeducedEdition } from "./PreviousEdition"

// Keys act outside the fields: typing in one is not a command (the page's keys read it too).
export const typing = target => ["INPUT", "SELECT", "TEXTAREA"].includes(target?.tagName) || target?.isContentEditable

// The proposal editor (ticket 15 Q2): a right drawer over the calendar, with Expand, Previous / Next through the List, and the tabs
// Event · Competitions · Answer · Warnings · History. Every command goes through the proposals module at the revision shown.
export default function ProposalDrawer({ target, row, query, settings, onClose, onCreated, onSaved, onPrevious, onNext, actions, warningActions,
  onRecordAnswer }) {
  const editing = target.mode === "edit"
  const read = useProposalRead(editing ? target.id : null)
  const proposal = read.proposal
  const operation = useProposalOperation()
  const [values, setValues] = useState(() => newForm(query, target.days))
  const [shownErrors, setShownErrors] = useState(false)
  const [tab, setTab] = useState("event")
  const [expanded, setExpanded] = useState(false)
  const [message, setMessage] = useState(null)
  const [leaving, setLeaving] = useState(null)
  // Changing the previous edition (ticket 09) is its own edit, from a proposal saved without unsaved changes.
  const [changingEdition, setChangingEdition] = useState(false)
  const organizerName = row?.organizer?.companyName || null
  useEffect(() => { if (proposal) setValues(formOf(proposal, organizerName)) }, [proposal, organizerName])
  // A drawer opened on a proposal takes the focus to its title, for keyboards and screen readers, and gives it back on closing.
  const heading = useRef(null)
  useEffect(() => { setTab("event"); setMessage(null); setShownErrors(false); heading.current?.focus() }, [target])
  useEffect(() => {
    const opener = document.activeElement
    return () => { if (opener && document.contains(opener)) opener.focus() }
  }, [])

  const errors = useMemo(() => formErrors(values), [values])
  const baseline = useMemo(() => JSON.stringify(proposal ? fieldsOf(formOf(proposal)) : newForm(query, target.days)), [proposal, query, target.days])
  const dirty = editing ? Boolean(proposal) && JSON.stringify(fieldsOf(values)) !== baseline : JSON.stringify(values) !== baseline
  const changes = useMemo(() => (proposal && dirty ? changesOf(proposal, values) : {}), [proposal, values, dirty])
  // A proposal is edited while it is open (Phase A6): a Ciornă, a Propusă or a Confirmată.
  const editable = !editing || OPEN_STATES.includes(proposal?.state)
  const timeZone = countryTimezone(proposal?.country || values.country) || "Europe/Bucharest"
  const context = { proposal: proposal || { sources: {} }, history: read.history || [], timeZone, settings, organizer: row?.organizer,
    competitions: proposal?.competitions }
  const sourceOf = path => (proposal ? valueSource(context, path) : null)
  const set = fields => setValues(previous => ({ ...previous, ...fields }))

  // Leaving with unsaved changes asks first, inside the drawer (CP lesson 2026-09-06).
  const leave = useCallback(go => (dirty ? setLeaving(() => go) : go()), [dirty])
  useEffect(() => {
    // Keys act outside the fields: Esc in a field (or an open list) stays with it.
    const keys = event => {
      if (event.key === "Escape" && !event.defaultPrevented && !typing(event.target)) leave(onClose)
      else if (!typing(event.target) && (event.key === "j" || event.key === "J") && onNext) leave(onNext)
      else if (!typing(event.target) && (event.key === "k" || event.key === "K") && onPrevious) leave(onPrevious)
    }
    document.addEventListener("keydown", keys)
    return () => document.removeEventListener("keydown", keys)
  }, [leave, onClose, onNext, onPrevious])

  const save = async () => {
    setShownErrors(true)
    setMessage(null)
    // A refused form opens the tab that holds the first field to fix.
    if (Object.keys(errors).length) return setTab(Object.keys(errors).every(key => key.startsWith("competitions.")) ? "competitions" : "event")
    // The competitions "Propose competitions" added are saved with their sources (D3).
    const sources = addedSources(values)
    const sourced = Object.keys(sources).length ? { sources } : {}
    const result = editing
      ? await operation.run("edit", "/admin/tournament-proposals/" + proposal.id, { revision: proposal.revision, changes, ...sourced }, "patch")
      : await operation.run("create", "/admin/tournament-proposals", { proposals: [{ ...draftOf(values), ...sourced }] })
    if (!result || ["cancelled", "busy"].includes(result.status)) return undefined
    if (result.status === "saved") {
      setShownErrors(false)
      if (!editing) return onCreated(result.proposals?.[0])
      setMessage({ tone: "success", text: "Saved. The change is in the proposal's history." })
      read.reload()
      return onSaved()
    }
    setMessage({ tone: "danger", text: result.status === "unknown" ? result.message || UNCONFIRMED : result.message })
    if (editing && (result.status === "unknown" || RELOADING_CODES.includes(result.code))) read.reload()
    return onSaved()
  }

  const title = editing ? (proposal ? `${proposal.number} · ${proposal.name || "No name yet"}` : row?.label || "Proposal") : "New proposal"
  const missing = missingToSend(row)
  const warnings = row?.warnings || []
  const tabs = [["event", "Event"], ["competitions", "Competitions"],
    ...(editing ? [["answer", "Answer"], ["warnings", `Warnings (${openWarnings(row || {}).length})`], ["history", "History"]] : [])]
  const confirmedChange = proposal?.state === "confirmed" && Object.keys(changes).some(field => CONFIRMED_FIELDS.includes(field))
  // The previous edition deduced (ticket 09) while the form has none.
  const deduced = useDeducedEdition({ editing, values, setValues, errors, proposalId: proposal?.id })
  // „Propose competitions” (ticket 11) reads the editor's values as they stand, from the previous edition.
  const previousEditionId = values.previousEdition?.eventId
  const propose = previousEditionId && {
    path: "/admin/tournament-proposal-editor/competitions?" + editorQuery(values, { ...(proposal && { proposalId: proposal.id }), previousEditionId }),
    disabledReason: errors.category || errors.startDay || errors.endDay ? "Choose the category and the dates first." : null,
  }
  const focusField = field => { setTab("event"); setTimeout(() => document.getElementById("proposal-" + field)?.focus(), 0) }

  return <>
    <div className="ep-scrim" aria-hidden="true" onClick={() => leave(onClose)} />
    <aside role="dialog" aria-modal="true" aria-labelledby="ep-drawer-title" className={"ep-drawer" + (expanded ? " is-expanded" : "")}>
      <header className="ep-drawer-head">
        <div className="ep-drawer-bar">
          {proposal && <span className={`ep-state ep-state-${proposal.state}`}>{stateLabel(proposal.state)}</span>}
          {(proposal?.category || values.category) && <span><span className={`ep-dot ep-cat-${proposal?.category || values.category}`} aria-hidden="true" />
            {categoryLabel(settings, proposal?.category || values.category)}</span>}
          <span className="ep-chip-static">Circuit: Sportya</span>
          <span className="ep-spacer" />
          <button type="button" className="btn btn-light btn-sm" onClick={() => setExpanded(!expanded)}>{expanded ? "Collapse" : "Expand"}</button>
          {onPrevious && <button type="button" className="ep-icon-button" aria-label="Previous proposal" onClick={() => leave(onPrevious)}>
            <i className="bx bx-chevron-left" aria-hidden="true" /></button>}
          {onNext && <button type="button" className="ep-icon-button" aria-label="Next proposal" onClick={() => leave(onNext)}>
            <i className="bx bx-chevron-right" aria-hidden="true" /></button>}
          <button type="button" className="ep-icon-button" aria-label="Close the editor" onClick={() => leave(onClose)}><i className="bx bx-x" aria-hidden="true" /></button>
        </div>
        <h2 id="ep-drawer-title" ref={heading} tabIndex={-1}>{title}</h2>
        {(!editing || proposal) && <PreviousEditionLine proposal={proposal} edition={values.previousEdition} deduced={deduced} context={context}
          canChange={editing && editable && !dirty && !operation.pending} onChange={() => setChangingEdition(true)} />}
        {proposal?.state === "draft" && missing && (missing.length
          ? <div className="ep-completeness"><span className="ep-meta">To send it needs:</span>
            {missing.map(field => <button type="button" key={field} className="ep-source is-missing" onClick={() => focusField(field)}>{SEND_FIELD_LABELS[field] || field}</button>)}</div>
          : <div className="ep-completeness"><span className="ep-mark ep-mark-ok">Ready to send</span></div>)}
        <div role="tablist" aria-label="Proposal sections" className="ep-tabs">
          {tabs.map(([key, label]) => <button type="button" role="tab" key={key} id={"ep-tab-" + key} aria-selected={tab === key}
            aria-controls="ep-drawer-panel" className={"ep-tab" + (tab === key ? " is-selected" : "")} onClick={() => setTab(key)}>{label}</button>)}
        </div>
      </header>
      <div className="ep-drawer-body" role="tabpanel" id="ep-drawer-panel" aria-labelledby={"ep-tab-" + tab}>
        {editing && read.error && <Alert color="danger">{read.error}{" "}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={read.reload}>Retry</button></Alert>}
        {editing && !proposal && !read.error && <div className="ep-loading"><Spinner size="sm" /> Loading the proposal…</div>}
        {(!editing || proposal) && <fieldset disabled={!editable || Boolean(operation.pending)}>
          {tab === "event" && <ProposalEventFields values={values} errors={shownErrors ? errors : {}} settings={settings} sourceOf={sourceOf}
            disabled={!editable || Boolean(operation.pending)} set={set} />}
          {tab === "competitions" && <ProposalCompetitions competitions={values.competitions} errors={shownErrors ? errors : {}} settings={settings}
            sourceOf={sourceOf} disabled={!editable || Boolean(operation.pending)} onChange={competitions => set({ competitions })} propose={propose} />}
        </fieldset>}
        {proposal && tab === "answer" && <AnswerTab proposal={proposal} row={row} settings={settings} timeZone={timeZone}
          onRecord={onRecordAnswer && (batch => leave(() => onRecordAnswer(batch)))} />}
        {proposal && tab === "warnings" && <WarningsTab warnings={warnings} timeZone={timeZone} actions={warningActions} />}
        {proposal && tab === "history" && <HistoryTab history={read.history || []}
          context={{ timeZone, settings, organizer: row?.organizer, competitions: proposal.competitions }} />}
      </div>
      <footer className="ep-drawer-foot">
        {message && <Alert color={message.tone} className="ep-drawer-message mb-0">{message.text}</Alert>}
        {leaving && <div className="ep-confirm" role="alertdialog" aria-label="Unsaved changes">
          <span>Discard the unsaved changes?</span>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => { const go = leaving; setLeaving(null); go() }}>Discard changes</button>
          <button type="button" className="btn btn-light btn-sm" onClick={() => setLeaving(null)}>Keep editing</button>
        </div>}
        {dirty && <span className="ep-mark ep-mark-soon">Unsaved changes</span>}
        {confirmedChange && <span className="ep-mark ep-mark-warn">Saving returns it to Proposed</span>}
        {proposal && actions(proposal, Boolean(operation.pending))}
        <span className="ep-spacer" />
        <button type="button" className="btn btn-light" onClick={() => leave(onClose)}>Cancel</button>
        {editable && <button type="button" className="btn btn-primary" onClick={save} disabled={Boolean(operation.pending) || (editing && (!proposal || !dirty))}>
          {operation.pending ? "Saving…" : editing ? "Save changes" : "Create proposal"}</button>}
      </footer>
    </aside>
    {changingEdition && proposal && <PreviousEditionDialog proposal={proposal} context={context} operation={operation} onClose={() => setChangingEdition(false)}
      onReload={read.reload} onSaved={() => {
        setChangingEdition(false)
        setMessage({ tone: "success", text: "Saved. The change is in the proposal's history." })
        read.reload()
        onSaved()
      }} />}
  </>
}
